import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle, Loader, Building2 } from 'lucide-react';
import api from '../api/apiClient';

interface InvitationDetails {
  valid: boolean;
  email?: string;
  name?: string;
  role?: string;
  agencyId?: number;
  expiresAt?: string;
  message?: string;
  error?: string;
}

export default function InviteAccept() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (token) {
      loadInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  const loadInvitation = async () => {
    try {
      const response = await api.get<InvitationDetails>(`/invite/${token}`);
      setInvitation(response);
      if (!response.valid) {
        setError(response.error || 'Invalid or expired invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post<{ success: boolean; message?: string; error?: string }>(`/invite/${token}/accept`, {
        password
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to accept invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const formatRole = (role: string) => {
    switch (role?.toUpperCase()) {
      case 'AGENT': return 'Property Agent';
      case 'RENTER': return 'Tenant';
      case 'OWNER': return 'Property Owner';
      case 'CONTRACTOR': return 'Contractor';
      case 'ADMIN': return 'Administrator';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-secondary" />
          <h2 className="text-2xl font-bold text-primary mb-2">Welcome Aboard!</h2>
          <p className="text-gray-600 mb-6">
            Your account has been created successfully. You can now log in and start using Rentry.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!invitation?.valid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">
            {error || 'This invitation link is invalid or has expired. Please contact your administrator for a new invitation.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h1 className="text-3xl font-bold text-primary mb-2">Accept Invitation</h1>
          <p className="text-gray-600">Create your account to join Rentry</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Invitation Details */}
          <div className="bg-secondary/10 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-primary mb-2">You've been invited!</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>Email:</strong> {invitation.email}</p>
              {invitation.name && <p><strong>Name:</strong> {invitation.name}</p>}
              <p><strong>Role:</strong> {formatRole(invitation.role || '')}</p>
            </div>
            {invitation.message && (
              <div className="mt-3 pt-3 border-t border-secondary/20">
                <p className="text-sm text-gray-600 italic">"{invitation.message}"</p>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-red-800">{error}</div>
            </div>
          )}

          {/* Password Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-1" /> Create Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-1" /> Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent ${
                  confirmPassword && password !== confirmPassword ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
              )}
            </div>

            <button
              onClick={handleAccept}
              disabled={submitting || password.length < 8 || password !== confirmPassword}
              className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" /> Creating Account...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" /> Accept & Create Account
                </>
              )}
            </button>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-secondary font-medium hover:underline">
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

